import { type StudentPaymentHistoryInvoice } from '~entities/student/api/get-student-payment-history';

import { normalizePayments } from '~shared/helpers/normalize-payments';

const manualPaymentMethods = new Set(['cash', 'check']);

export type PaymentHistoryPaymentRow = {
  method?: string | null;
  amount?: number;
  reversedAt?: string | Date | null;
  isReversal?: boolean;
  /** Stripe/provider charge id — required before offering card Refund (ENR-4064). */
  paymentIntentId?: string | null;
};

export type PaymentHistoryRefundAction =
  | { type: 'none' }
  | { type: 'reattempt' }
  | { type: 'scheduled-failed' }
  | {
      type: 'refund';
      manualAmount: number;
      onlineAmount: number;
      onlineMode: 'online' | 'stripe';
    };

const getNormalizedPaymentMethod = (payment: { method?: string | null }) =>
  payment.method?.toLowerCase() ?? '';

export const isUnreversedPositivePayment = (
  payment: PaymentHistoryPaymentRow,
) => {
  const amount = Number(payment.amount ?? 0);
  if (amount <= 0 || payment.isReversal) {
    return false;
  }
  return !payment.reversedAt;
};

export const getActiveManualPaidAmount = (
  payments: Array<PaymentHistoryPaymentRow>,
) =>
  normalizePayments(payments)
    .filter((payment) =>
      manualPaymentMethods.has(getNormalizedPaymentMethod(payment)),
    )
    .filter(isUnreversedPositivePayment)
    .reduce((sum, payment) => sum + Number(payment.amount), 0);

export const getActiveNonManualPaidAmount = (
  payments: Array<PaymentHistoryPaymentRow>,
) =>
  normalizePayments(payments)
    .filter(
      (payment) =>
        !manualPaymentMethods.has(getNormalizedPaymentMethod(payment)),
    )
    .filter(isUnreversedPositivePayment)
    // Card/ledger rows without a captured PaymentIntent are not refundable
    // online (false Paid private-lesson rows had CARD + no PI).
    .filter((payment) => Boolean(payment.paymentIntentId?.trim()))
    .reduce((sum, payment) => sum + Number(payment.amount), 0);

export const hasReversalHistory = (
  payments: Array<PaymentHistoryPaymentRow>,
) =>
  normalizePayments(payments).some(
    (payment) => Boolean(payment.isReversal) || Boolean(payment.reversedAt),
  );

export const hasManualCashCheckInLedger = (
  payments: Array<PaymentHistoryPaymentRow>,
  paymentMethod?: string,
) =>
  manualPaymentMethods.has(paymentMethod?.toLowerCase() ?? '') ||
  normalizePayments(payments).some((payment) =>
    manualPaymentMethods.has(getNormalizedPaymentMethod(payment)),
  );

/** Strip embedded Mongo invoice refs from credit-memo product labels. */
export const formatPaymentHistoryProductLabel = (className?: string | null) => {
  const raw = className?.trim() ?? '';
  if (!raw) {
    return 'N/A';
  }

  const withoutInvoiceRef = raw
    .replace(/\s*\(Invoice\s*#[^)]+\)\s*/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (/^Refund:\s*Cash returned to client$/i.test(withoutInvoiceRef)) {
    return 'Cash reversal';
  }
  if (/^Refunded:\s*Check voided$/i.test(withoutInvoiceRef)) {
    return 'Check reversal';
  }

  return withoutInvoiceRef || raw;
};

export const resolvePaymentHistoryRefundAction = (
  invoice: Pick<
    StudentPaymentHistoryInvoice,
    '_id' | 'status' | 'payments' | 'paymentMethod'
  >,
): PaymentHistoryRefundAction => {
  if (!invoice._id) {
    return { type: 'none' };
  }

  const payments = normalizePayments(invoice.payments);
  const activeManualAmount = getActiveManualPaidAmount(payments);
  const activeOnlineAmount = getActiveNonManualPaidAmount(payments);
  const hasRemainingReversalActions =
    hasReversalHistory(payments) &&
    (activeManualAmount > 0 || activeOnlineAmount > 0);
  const shouldShowRefund =
    invoice.status === 'paid' ||
    (invoice.status === 'partially paid' && hasRemainingReversalActions);

  if (!shouldShowRefund) {
    if (invoice.status === 'partially paid') {
      return { type: 'reattempt' };
    }

    if (
      invoice.status === 'scheduled' ||
      invoice.status === 'invoice scheduled' ||
      invoice.status === 'failed' ||
      invoice.status === 'pending' ||
      invoice.status === 'free' ||
      invoice.status === 'invalid'
    ) {
      return { type: 'scheduled-failed' };
    }

    return { type: 'none' };
  }

  // Paid/Card with no captured PI and no cash/check ledger → do not offer Refund
  if (activeManualAmount <= 0 && activeOnlineAmount <= 0) {
    return { type: 'none' };
  }

  const hasManualInLedger = hasManualCashCheckInLedger(
    payments,
    invoice.paymentMethod,
  );
  const onlineMode: 'online' | 'stripe' =
    activeOnlineAmount > 0 &&
    activeManualAmount <= 0 &&
    hasManualInLedger
      ? 'online'
      : 'stripe';

  return {
    type: 'refund',
    manualAmount: activeManualAmount,
    onlineAmount: activeOnlineAmount,
    onlineMode,
  };
};
